import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';

import { Breadcrumb } from '@/components/BreadCrumbs';
import CustomModal from '@/components/CustomModal';
import UserDetailsSkeleton from '@/components/SkeletonLoader/UserDetailsSkeleton';
import { ERoleAccess, ECommonStatus, ERestoreEntityType } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import { blockUnblockRecordAPI } from '@/service/commonService';
import { getChildDetails, getParentDetails } from '@/service/schoolService';
import useCommonStore from '@/store/useCommonStore';
import { IChildDetails, IParentDetails } from '@/types';

import ChildDetailsContent from './ChildDetailsContent';
import ParentDetailsContent from './ParentDetailsContent';

type TView = 'parent' | 'child';

interface IParentDetailsModalProps {
  setIsOpen: (open: boolean) => void;
  selectedParentId: string | undefined;
  onStatusUpdate: () => void;
}

const ParentDetailsModal = ({
  setIsOpen,
  selectedParentId,
  onStatusUpdate,
}: IParentDetailsModalProps) => {
  const { hasAccess } = useAccess();

  const { loadingModal, setLoadingModal } = useCommonStore();
  const { formatText } = useFormatText();

  const [parentDetails, setParentDetails] = useState<IParentDetails | null>(
    null
  );
  const [child, setChild] = useState<IChildDetails | null>(null);

  const [view, setView] = useState<TView>('parent');
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);

  const fetchParentDetails = async () => {
    if (!selectedParentId) return;

    setLoadingModal(true);
    try {
      const response = await getParentDetails(selectedParentId);
      if (response?.status) {
        setParentDetails(response?.data);
      } else {
        console.error(response?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingModal(false);
    }
  };

  const fetchChildDetails = async () => {
    if (!selectedChildId) return;

    setLoadingModal(true);
    try {
      const response = await getChildDetails(selectedChildId);
      if (response?.status) {
        setChild(response?.data);
      } else {
        console.error(response?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingModal(false);
    }
  };

  useEffect(() => {
    fetchParentDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (view === 'child' && selectedChildId) {
      fetchChildDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selectedChildId]);

  const handleChildClick = (childId: string) => {
    setSelectedChildId(childId);
    setView('child');
  };

  const handleClose = () => {
    setView('parent');
    setSelectedChildId(null);
    setIsOpen(false);
  };

  const handleBlockParent = async () => {
    if (!parentDetails?.id) return;

    try {
      const params = {
        id: parentDetails.id,
        record_type: ERestoreEntityType.PARENT,
        is_block: parentDetails.status === ECommonStatus.ACTIVE,
      };
      const response = await blockUnblockRecordAPI(params);
      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        onStatusUpdate();
        fetchParentDetails();
      } else {
        console.error(response?.message || 'Failed to update parent status');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleBlockChild = async () => {
    if (!child?.id) return;
    try {
      const params = {
        id: child.id,
        record_type: ERestoreEntityType.CHILD,
        is_block: child.status === ECommonStatus.ACTIVE,
      };
      const response = await blockUnblockRecordAPI(params);
      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        fetchChildDetails();
      } else {
        console.error(response?.message || 'Failed to update child status');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getBreadcrumbPath = () => [
    {
      label: formatText('parent.parent-details'),
      href: 'parent',
    },
    {
      label: formatText('parent.child-details'),
      href: 'child',
    },
  ];

  const headerContent =
    view === 'parent' ? (
      <h2 className="text-neutral-primary font-medium leading-7 text-lg">
        {formatText('parent.parent-details')}
      </h2>
    ) : (
      <Breadcrumb
        path={getBreadcrumbPath()}
        onNavigateRequest={(href) => {
          if (href === 'parent') {
            setView('parent');
            setSelectedChildId(null);
          }
        }}
      />
    );

  if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

  return (
    <CustomModal
      setIsOpen={handleClose}
      isHeader
      position="right"
      headercontent={headerContent}
      loadingModal={loadingModal}
    >
      {loadingModal ? (
        <UserDetailsSkeleton />
      ) : view === 'parent' ? (
        <ParentDetailsContent
          parentDetails={parentDetails}
          onChildClick={handleChildClick}
          handleBlockParent={handleBlockParent}
        />
      ) : (
        <ChildDetailsContent
          child={child}
          handleBlockChild={handleBlockChild}
        />
      )}
    </CustomModal>
  );
};

export default ParentDetailsModal;
